import React, { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, X, GitCompareArrows, ArrowLeft, ArrowRight, Crown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { COMPARISONS } from "@/data/comparisons";
import usePageSeo from "@/hooks/usePageSeo";
import AboutDppCard from "@/components/AboutDppCard";

/**
 * `/vs/field` — the single "DPP vs. the entire field" mega-scoreboard.
 *
 * Merges every row from every competitor entry in `data/comparisons.js` into
 * a unified feature matrix. For each unique row-name we emit the DPP value
 * once + one column per competitor that also scores that feature. Rows the
 * competitors don't rate get an em-dash so the table still lines up.
 *
 * The page complements the leaf `/vs/:slug` pages (deep, narrative
 * comparisons for long-tail SEO) by giving Google + AI answers a single URL
 * that answers "how does DPP compare against everything at once?".
 */
export default function CompareField() {
  const nav = useNavigate();
  const competitors = Object.values(COMPARISONS);
  const [searchParams] = useSearchParams();
  // When the visitor arrives from a landing "Why-DPP" tile we surface a
  // dedicated "Return to landing page" arrow so they can bounce back with
  // one tap. Also auto-scrolls to the anchored area row on mount.
  const fromLanding = searchParams.get("from") === "landing";

  React.useEffect(() => {
    if (!fromLanding) return;
    // Give the sticky nav + JSON-LD a beat to hydrate before scrolling.
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-primary/50", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary/50", "ring-offset-2"), 2400);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [fromLanding]);

  usePageSeo({
    title: "DPP vs. the field — Every dividend planner in one scorecard",
    description:
      "Full side-by-side scorecard: DPP vs Snowball, Sharesight, Seeking Alpha, and every other dividend tracker — features, pricing, data.",
    path: "/vs/field",
    ogSlug: "vs-field",
    jsonLdId: "compare-field-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          "@id": "https://dividendportfolioplanner.com/vs/field#itemlist",
          name: "DPP vs. the field — best dividend portfolio planner scorecard",
          description:
            "Unified scorecard comparing Dividend Portfolio Planner against every major dividend tracker on cost, AI, backtesting, holdings, dividends, and research.",
          itemListElement: competitors.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.competitor_name,
            url: `https://dividendportfolioplanner.com/vs/${c.slug}`,
          })),
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://dividendportfolioplanner.com/vs/field#breadcrumbs",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",    item: "https://dividendportfolioplanner.com/" },
            { "@type": "ListItem", position: 2, name: "Compare", item: "https://dividendportfolioplanner.com/vs" },
            { "@type": "ListItem", position: 3, name: "DPP vs. the field", item: "https://dividendportfolioplanner.com/vs/field" },
          ],
        },
      ],
    },
  });

  // Merge every row from every competitor. Rows are keyed by (area, name) so
  // duplicates across competitors collapse into one row of the master table.
  // DPP value is taken from the first competitor that mentions the row
  // (they're all internally consistent).
  const matrix = useMemo(() => {
    const byKey = new Map();
    for (const c of competitors) {
      for (const r of c.rows || []) {
        const key = `${r.area}::${r.name}`;
        if (!byKey.has(key)) {
          byKey.set(key, { area: r.area, name: r.name, dpp: r.dpp, byCompetitor: {} });
        }
        byKey.get(key).byCompetitor[c.slug] = r.competitor;
      }
    }
    // Preserve area ordering: Cost, AI, Analysis, Holdings, Dividends, Research, Tax, Platform, Brokers, Other
    const areaOrder = ["Cost", "AI", "Analysis", "Holdings", "Dividends", "Research", "Tax", "Platform", "Brokers"];
    const rank = (a) => {
      const i = areaOrder.indexOf(a);
      return i === -1 ? areaOrder.length : i;
    };
    return Array.from(byKey.values()).sort((a, b) => rank(a.area) - rank(b.area));
  }, [competitors]);

  // Group merged rows by area for the section-based rendering.
  const grouped = useMemo(() => {
    const g = {};
    for (const r of matrix) (g[r.area] ||= []).push(r);
    return g;
  }, [matrix]);

  const renderCell = (v) => {
    if (v === true) return <Check className="w-4 h-4 text-primary mx-auto" />;
    if (v === false) return <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />;
    if (v === undefined) return <span className="text-muted-foreground/40">—</span>;
    return <span className="text-xs sm:text-sm">{v}</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="compare-field-page">
      {/* Sticky "back to Compare hub" nav — mirrors the leaf pages so users
          never have to scroll or reopen the hamburger to move sideways.
          When the visitor arrived from a landing tile we prepend a
          "Return to landing" arrow so the round-trip is one tap. */}
      <div
        className="sticky top-0 z-30 -mx-6 px-6 py-3 mb-6 border-b border-border/70 bg-background/85 backdrop-blur"
        data-testid="field-back-nav"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {fromLanding && (
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                data-testid="field-back-to-landing"
                title="Return to the landing page"
              >
                <ArrowLeft className="w-4 h-4" /> Return to landing page
              </Link>
            )}
            <Link
              to="/vs"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="field-back-to-hub"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Compare
            </Link>
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">
            DPP vs. the field
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-secondary">
          <GitCompareArrows className="w-3.5 h-3.5" /> The full scorecard
        </div>
        <h1
          className="serif text-4xl sm:text-5xl lg:text-6xl mt-3"
          data-testid="compare-field-heading"
        >
          DPP vs. <span className="italic text-primary">the entire field</span>.
        </h1>
        <p className="text-muted-foreground mt-5 text-base sm:text-lg leading-relaxed">
          Every feature. Every price. All 5 competitors, one chart. Below is the
          complete side-by-side scorecard aggregating our head-to-head research
          against Snowball Analytics, Sharesight, Seeking Alpha, Track Your
          Dividends, and Dividend Tracker.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20">
          <Crown className="w-3.5 h-3.5" /> {competitors.length} apps benchmarked · {matrix.length} unique features
        </div>
      </div>

      {/* Master scoreboard */}
      <Card className="mt-12" data-testid="compare-field-scorecard">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[880px]">
              <thead className="bg-muted/40 sticky top-[52px] z-20">
                <tr>
                  <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest text-muted-foreground w-56">
                    Feature
                  </th>
                  <th className="text-center px-3 py-3 mono text-[10px] uppercase tracking-widest text-primary">
                    DPP
                  </th>
                  {competitors.map((c) => (
                    <th
                      key={c.slug}
                      className="text-center px-3 py-3 mono text-[10px] uppercase tracking-widest text-muted-foreground"
                      title={c.hero_subtitle}
                    >
                      <Link to={`/vs/${c.slug}`} className="hover:text-primary transition-colors">
                        {c.competitor_name}
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([area, rows]) => (
                  <React.Fragment key={area}>
                    <tr
                      className="bg-primary/[0.03] scroll-mt-28 transition-shadow"
                      id={`area-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      data-testid={`field-area-${area.toLowerCase()}`}
                    >
                      <td colSpan={competitors.length + 2} className="px-4 py-2 mono text-[10px] uppercase tracking-widest text-secondary">
                        {area}
                      </td>
                    </tr>
                    {rows.map((r) => (
                      <tr
                        key={`${r.area}::${r.name}`}
                        className="border-t border-border/60"
                        data-testid={`field-row-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                      >
                        <td className="px-4 py-3 text-sm">{r.name}</td>
                        <td className="text-center px-3 py-3 bg-primary/[0.04]">{renderCell(r.dpp)}</td>
                        {competitors.map((c) => (
                          <td key={c.slug} className="text-center px-3 py-3">
                            {renderCell(r.byCompetitor[c.slug])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Analysis prose */}
      <section
        aria-labelledby="field-analysis-heading"
        className="mt-14 max-w-3xl mx-auto"
        data-testid="compare-field-analysis"
      >
        <h2 id="field-analysis-heading" className="serif text-2xl sm:text-3xl mb-4">
          The analysis
        </h2>
        <div className="prose prose-neutral dark:prose-invert max-w-none text-sm sm:text-base leading-relaxed">
          <p>
            Across every competitor DPP is the <b>only tool</b> that ships a 30+
            year backtest engine, an unlimited AI Optimizer, an AI Income Target
            Planner that reverse-solves a portfolio to a monthly-income goal,
            and portfolio grouping across brokerages — all at{" "}
            <b>$9.99/mo Pro</b> (or $95.99/yr, 20% off). Snowball ($14/mo),
            Sharesight ($15/mo), and Seeking Alpha ($19.92/mo) are all pricier
            for feature sets that lack one or more of those four capabilities.
            Track Your Dividends matches DPP's monthly price but offers no
            backtest and no AI planning. Dividend Tracker (DivTracker) is
            cheaper ($4.99–$6.99/mo) but is a manual cash-flow logger, not a
            planner.
          </p>
          <h3>Where each competitor still wins</h3>
          <ul>
            {competitors.map((c) => (
              <li key={c.slug}>
                <b>{c.competitor_name}</b>:{" "}
                {c.prose_when_snowball ||
                  c.prose_when_sharesight ||
                  c.prose_when_seekingalpha ||
                  c.prose_when_tyd ||
                  c.prose_when_divtracker ||
                  "See the leaf comparison."}
                {" "}
                <Link to={`/vs/${c.slug}`} className="underline underline-offset-2">
                  Read full head-to-head →
                </Link>
              </li>
            ))}
          </ul>
          <h3>Bottom line</h3>
          <p>
            If your primary need is <b>planning + backtesting + AI on a real
            portfolio</b>, DPP is the tool that ships the most for the money.
            If you need international tax reports, keep Sharesight; if you need
            author-driven research, keep Seeking Alpha; if the cheapest mobile
            cash-flow tracker is all you want, DivTracker wins. Everyone else,
            start with DPP free.
          </p>
        </div>
      </section>

      {/* Ready to try — direct CTA to Compare Chart (the app's flagship
          side-by-side feature) since users on this page have already opted in
          to a compare-mindset. */}
      <div className="mt-14 text-center">
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Ready to run your own numbers?</div>
        <h2 className="serif text-3xl sm:text-4xl mt-2">Compare your portfolios inside DPP.</h2>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
          Load any two of your own portfolios into the DPP Compare Chart and
          see the 30-year cumulative-return line, dividend income growth,
          drawdown, and CAGR side-by-side.
        </p>
        <div className="mt-5 flex justify-center gap-3 flex-wrap">
          <Button className="rounded-full" onClick={() => nav("/compare")} data-testid="compare-field-cta">
            <GitCompareArrows className="w-4 h-4 mr-2" /> Open the Compare Chart
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => nav("/pricing")}>
            See full pricing <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      <AboutDppCard />
    </div>
  );
}
